import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash, PaperPlaneTilt, PencilSimple, WarningCircle, Hourglass, CheckCircle, ClockCounterClockwise, Airplane, CaretUpDown, Check, CalendarBlank } from "@phosphor-icons/react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = (d) => d.slice(0, 7);

// ISO week string "YYYY-Www" matching the backend _iso_week format.
function isoWeek(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = (d.getUTCDay() + 6) % 7;         // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - day + 3);       // nearest Thursday
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

// 24h "HH:MM" <-> 12h parts
function to12(hhmm) {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return { h12: String(h % 12 || 12), m: String(m).padStart(2, "0"), ampm: h >= 12 ? "PM" : "AM" };
}
function to24(h12, m, ampm) {
  let h = Number(h12) % 12;
  if (ampm === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${m}`;
}

// Human-readable submission deadline for a given entry date + cutoff hours.
// Deadline = IST midnight of the next day + cutoff hours; shown as an exact IST clock time.
function deadlineLabel(dateStr, cutoffHours) {
  if (!dateStr) return "";
  // IST midnight of the entry date, as an absolute instant.
  const start = new Date(`${dateStr}T00:00:00+05:30`).getTime();
  const deadline = new Date(start + (24 + Number(cutoffHours || 0)) * 3600000);
  return deadline.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
  }) + " IST";
}

// Is the submission window still open for a date? Mirrors backend submission_open (IST).
function windowOpen(dateStr, cutoffHours) {
  if (!dateStr) return true;
  const start = new Date(`${dateStr}T00:00:00+05:30`).getTime();
  const deadline = start + (24 + Number(cutoffHours || 0)) * 3600000;
  return Date.now() <= deadline;
}

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground border-border",
  submitted: "bg-rag-amber-bg text-rag-amber-fg border-rag-amber/30",
  approved: "bg-rag-green-bg text-rag-green-fg border-rag-green/30",
  rejected: "bg-rag-red-bg text-rag-red-fg border-rag-red/30",
};

function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) mins += 1440;
  return Math.round((mins / 60) * 100) / 100;
}

const emptyForm = { task_id: "", client_id: "", description: "", location: "", start_time: "09:00", end_time: "17:00" };

export default function Timesheet() {
  const [searchParams] = useSearchParams();
  const initialDate = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get("date") || "") ? searchParams.get("date") : todayStr();
  const [date, setDate] = useState(initialDate);
  const [options, setOptions] = useState({ tasks: [], clients: [], job_role_name: null, submission_cutoff_hours: 24 });
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [taskOpen, setTaskOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [lateReq, setLateReq] = useState(null);
  const [leave, setLeave] = useState(null);
  const [holiday, setHoliday] = useState(null);
  const [reqDialog, setReqDialog] = useState(false);
  const [reqReason, setReqReason] = useState("");
  const dateRef = useRef(date);
  useEffect(() => { dateRef.current = date; }, [date]);

  const loadEntries = useCallback(async () => {
    const { data } = await api.get("/timesheet/entries", { params: { date_from: date, date_to: date } });
    if (dateRef.current !== date) return;
    setEntries(data);
  }, [date]);

  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/timesheet/summary", { params: { month: monthStr(date) } });
    if (dateRef.current !== date) return;
    setSummary(data);
  }, [date]);

  const loadLateReq = useCallback(async () => {
    const { data } = await api.get("/timesheet/late-requests", { params: { date } });
    if (dateRef.current !== date) return;
    setLateReq(data[0] || null);
  }, [date]);

  const loadLeave = useCallback(async () => {
    const { data } = await api.get("/timesheet/leaves", { params: { date_from: date, date_to: date } });
    if (dateRef.current !== date) return;
    setLeave(data[0] || null);
  }, [date]);

  const loadHoliday = useCallback(async () => {
    const { data } = await api.get("/timesheet/my-holidays", { params: { date_from: date, date_to: date } });
    if (dateRef.current !== date) return;
    setHoliday(data[0] || null);
  }, [date]);

  useEffect(() => { api.get("/timesheet/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadEntries(); loadSummary(); loadLateReq(); loadLeave(); loadHoliday(); }, [loadEntries, loadSummary, loadLateReq, loadLeave, loadHoliday]);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const saveEntry = async () => {
    if (!form.task_id) return toast.error("Pick a task");
    if (!form.start_time || !form.end_time) return toast.error("Start and end time are required");
    const payload = { ...form, date, client_id: form.client_id || null, location: form.location || null };
    try {
      if (editingId) {
        await api.put(`/timesheet/entries/${editingId}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/timesheet/entries", payload);
        toast.success("Entry added");
      }
      resetForm();
      loadEntries(); loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save entry");
    }
  };

  const editEntry = (e) => {
    setEditingId(e.id);
    setForm({ task_id: e.task_id, client_id: e.client_id || "", description: e.description || "",
      location: e.location || "",
      start_time: e.start_time, end_time: e.end_time });
  };

  const deleteEntry = async (id) => {
    try { await api.delete(`/timesheet/entries/${id}`); loadEntries(); loadSummary(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const submitDay = async () => {
    try {
      const { data } = await api.post("/timesheet/entries/submit", { date });
      toast.success(`Submitted ${data.submitted} entr${data.submitted === 1 ? "y" : "ies"}${data.late ? " (late)" : ""} for approval`);
      loadEntries(); loadSummary(); loadLateReq();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    }
  };

  const requestLate = async () => {
    try {
      await api.post("/timesheet/late-requests", { date, reason: reqReason });
      toast.success("Late submission requested — your manager will review it");
      setReqDialog(false); setReqReason("");
      loadLateReq();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    }
  };

  const markLeave = async () => {
    try {
      await api.post("/timesheet/leaves", { date });
      toast.success("Marked as leave — no timesheet needed for this day");
      loadLeave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark leave");
    }
  };

  const removeLeave = async () => {
    if (!leave) return;
    try {
      await api.delete(`/timesheet/leaves/${leave.id}`);
      toast.success("Leave removed");
      loadLeave();
    } catch (e) {
      toast.error("Could not remove leave");
    }
  };

  const dayTotal = Math.round(entries.reduce((s, e) => s + (e.total_hours || 0), 0) * 100) / 100;
  const weekTotal = (summary?.weeks || []).find((w) => w.week === isoWeek(date))?.hours || 0;
  const draftCount = entries.filter((e) => ["draft", "rejected"].includes(e.status)).length;
  const preview = calcHours(form.start_time, form.end_time);
  const noJobRole = options.tasks.length === 0;
  const closed = !windowOpen(date, options.submission_cutoff_hours);
  const lateApproved = closed && lateReq?.status === "approved";
  const latePending = closed && lateReq?.status === "pending";
  const lateRejected = closed && lateReq?.status === "rejected";

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-6xl" data-testid="timesheet-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">My Timesheet</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {options.job_role_name ? <>Role: <span className="text-foreground">{options.job_role_name}</span> · </> : null}
            {closed
              ? <>Submission window for <span className="text-rag-red-fg">{date}</span> has closed.</>
              : <>Submit before <span className="text-foreground">{deadlineLabel(date, options.submission_cutoff_hours)}</span>.</>}
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)}
              data-testid="ts-date-input" className="w-44" />
          </div>
          {holiday ? (
            <div className="text-xs text-sky-500 font-mono self-end pb-2" data-testid="ts-holiday-note">Company holiday</div>
          ) : leave ? (
            <Button variant="outline" onClick={removeLeave} data-testid="ts-remove-leave-btn"
              className="border-border text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground">Cancel Leave</Button>
          ) : (
            <Button variant="outline" onClick={markLeave} disabled={entries.length > 0}
              data-testid="ts-mark-leave-btn" title={entries.length > 0 ? "Remove entries before marking leave" : "Mark this day as leave"}>Mark as Leave</Button>
          )}
        </div>
      </div>

      {holiday && (
        <div className="flex items-center gap-2 text-sm text-sky-600 dark:text-sky-400 bg-sky-500/10 border border-sky-500/30 rounded-sm px-3 py-2.5" data-testid="ts-holiday-banner">
          <CalendarBlank size={16} weight="fill" />
          <span><b>{holiday.name}</b> — {date} is a company holiday. No timesheet entry needed; it won't be flagged as missing.</span>
        </div>
      )}

      {leave && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted border border-border rounded-sm px-3 py-2.5" data-testid="ts-leave-banner">
          <Airplane size={16} weight="fill" />
          <span>You're marked <b>On Leave</b> for <b>{date}</b> — no timesheet needed. It won't be flagged as missing.</span>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="ts-summary">
          <Card className="p-4"><div className="text-xs text-muted-foreground">Day total</div>
            <div className="font-display text-2xl mt-1" data-testid="ts-day-total">{dayTotal}h</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">This week ({isoWeek(date)})</div>
            <div className="font-display text-2xl mt-1" data-testid="ts-week-total">{weekTotal}h</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">This month ({summary.month})</div>
            <div className="font-display text-2xl mt-1" data-testid="ts-month-total">{summary.month_total}h</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Approved (month)</div>
            <div className="font-display text-2xl mt-1 text-rag-green-fg">{summary.by_status?.approved || 0}h</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Awaiting approval</div>
            <div className="font-display text-2xl mt-1 text-rag-amber-fg">{summary.by_status?.submitted || 0}h</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Late submissions (month)</div>
            <div className={`font-display text-2xl mt-1 ${summary.late_count ? "text-rag-red-fg" : ""}`} data-testid="ts-late-count">{summary.late_count || 0}</div></Card>
        </div>
      )}

      <div className="space-y-6">
        {/* Entry form — full width, horizontal */}
        <Card className="p-5 space-y-4" data-testid="ts-entry-form">
          <div className="font-display text-lg">{editingId ? "Edit entry" : "Add entry"}</div>
          {noJobRole && (
            <div className="flex items-start gap-2 text-sm text-rag-amber-fg bg-rag-amber-bg border border-rag-amber/30 rounded-sm p-3">
              <WarningCircle size={16} className="mt-0.5 shrink-0" />
              No tasks available yet. Ask your admin to assign you a job role.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs">Task</Label>
              <Popover open={taskOpen} onOpenChange={setTaskOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    data-testid="ts-task-select"
                    className="w-full justify-between font-normal rounded-sm mt-0"
                  >
                    <span className={form.task_id ? "" : "text-muted-foreground"}>
                      {options.tasks.find((t) => t.id === form.task_id)?.name || "Select task"}
                    </span>
                    <CaretUpDown size={14} className="opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command>
                    <CommandInput placeholder="Search tasks…" data-testid="ts-task-search" className="text-sm" />
                    <CommandList>
                      <CommandEmpty>No matching task.</CommandEmpty>
                      {options.tasks.map((t) => (
                        <CommandItem
                          key={t.id}
                          value={t.name}
                          data-testid={`ts-task-option-${t.id}`}
                          onSelect={() => { setForm({ ...form, task_id: t.id }); setTaskOpen(false); }}
                        >
                          <Check size={14} className={`mr-2 ${form.task_id === t.id ? "opacity-100 text-primary" : "opacity-0"}`} />
                          {t.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs">Client</Label>
              <Popover open={clientOpen} onOpenChange={setClientOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    data-testid="ts-client-select"
                    className="w-full justify-between font-normal rounded-sm"
                  >
                    <span className={form.client_id ? "" : "text-muted-foreground"}>
                      {options.clients.find((c) => c.id === form.client_id)?.name || "— None —"}
                    </span>
                    <CaretUpDown size={14} className="opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients…" data-testid="ts-client-search" className="text-sm" />
                    <CommandList>
                      <CommandEmpty>No matching client.</CommandEmpty>
                      <CommandItem value="— None —" data-testid="ts-client-option-none" onSelect={() => { setForm({ ...form, client_id: "" }); setClientOpen(false); }}>
                        <Check size={14} className={`mr-2 ${!form.client_id ? "opacity-100 text-primary" : "opacity-0"}`} />
                        — None —
                      </CommandItem>
                      {options.clients.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          data-testid={`ts-client-option-${c.id}`}
                          onSelect={() => { setForm({ ...form, client_id: c.id }); setClientOpen(false); }}
                        >
                          <Check size={14} className={`mr-2 ${form.client_id === c.id ? "opacity-100 text-primary" : "opacity-0"}`} />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Select value={form.location || "none"} onValueChange={(v) => setForm({ ...form, location: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ts-location-select"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  <SelectItem value="Office">Office</SelectItem>
                  <SelectItem value="Home">Home</SelectItem>
                  <SelectItem value="ClientPlace">Client Place</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What did you work on?" data-testid="ts-description-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end mt-3">
            <div>
              <Label className="text-xs">Start</Label>
              <TimeField value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testid="ts-start-input" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <TimeField value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testid="ts-end-input" />
            </div>
            <div>
              <Label className="text-xs">Total</Label>
              <div className="h-10 flex items-center font-display text-lg" data-testid="ts-hours-preview">{preview}h</div>
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEntry} disabled={noJobRole} data-testid="ts-save-entry-btn" className="flex-1">
                {editingId ? "Update" : "Add"}
              </Button>
            </div>
          </div>
          {editingId && (
            <div><Button variant="outline" size="sm" onClick={resetForm} data-testid="ts-cancel-edit-btn">Cancel edit</Button></div>
          )}
        </Card>

        {/* Entries list — full width, below the form */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-display text-lg">Entries for {date}</span>
              {closed && <Badge variant="outline" className="bg-rag-red-bg text-rag-red-fg border-rag-red/30" data-testid="ts-window-closed-badge">Window closed</Badge>}
            </div>
            {lateApproved ? (
              <Button size="sm" onClick={submitDay} disabled={draftCount === 0} data-testid="ts-submit-day-btn">
                <PaperPlaneTilt size={14} className="mr-1.5" /> Submit day — late ({draftCount})
              </Button>
            ) : closed ? (
              <Button size="sm" variant="outline" onClick={() => setReqDialog(true)}
                disabled={draftCount === 0 || latePending} data-testid="ts-request-late-btn">
                <ClockCounterClockwise size={14} className="mr-1.5" />
                {latePending ? "Requested" : "Request late submission"}
              </Button>
            ) : (
              <Button size="sm" onClick={submitDay} disabled={draftCount === 0} data-testid="ts-submit-day-btn">
                <PaperPlaneTilt size={14} className="mr-1.5" /> Submit day ({draftCount})
              </Button>
            )}
          </div>

          {latePending && (
            <div className="flex items-start gap-2 text-sm text-rag-amber-fg bg-rag-amber-bg border border-rag-amber/30 rounded-sm px-3 py-2.5" data-testid="ts-late-pending-banner">
              <Hourglass size={16} className="mt-0.5 shrink-0" />
              <span>Late submission requested for <b>{date}</b> — awaiting your manager's approval.</span>
            </div>
          )}
          {lateApproved && (
            <div className="flex items-start gap-2 text-sm text-rag-green-fg bg-rag-green-bg border border-rag-green/30 rounded-sm px-3 py-2.5" data-testid="ts-late-approved-banner">
              <CheckCircle size={16} className="mt-0.5 shrink-0" />
              <span>Your manager approved a late submission for <b>{date}</b>. Submit now — it will be flagged as late and counts toward your monthly late total.</span>
            </div>
          )}
          {lateRejected && (
            <div className="flex items-start gap-2 text-sm text-rag-red-fg bg-rag-red-bg border border-rag-red/30 rounded-sm px-3 py-2.5" data-testid="ts-late-rejected-banner">
              <WarningCircle size={16} className="mt-0.5 shrink-0" />
              <span>Your late-submission request for <b>{date}</b> was declined{lateReq?.manager_comment ? `: ${lateReq.manager_comment}` : ""}.</span>
            </div>
          )}
          {closed && !lateReq && draftCount > 0 && (
            <div className="flex items-start gap-2 text-sm text-rag-red-fg bg-rag-red-bg border border-rag-red/30 rounded-sm px-3 py-2.5" data-testid="ts-window-closed-banner">
              <WarningCircle size={16} className="mt-0.5 shrink-0" />
              <span>The submission window for <b>{date}</b> closed (deadline was {options.submission_cutoff_hours}h after the day ended). Request a late submission and your manager can approve it.</span>
            </div>
          )}
          {entries.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="ts-empty">No entries for this date yet.</Card>
          ) : (
            <Card className="overflow-hidden" data-testid="ts-entries-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="whitespace-nowrap">Start</TableHead>
                    <TableHead className="whitespace-nowrap">End</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} data-testid={`ts-entry-${e.id}`}>
                      <TableCell className="align-top">
                        <div className="font-medium">{e.task_name}</div>
                      </TableCell>
                      <TableCell className="align-top text-sm">{e.client_name || "—"}</TableCell>
                      <TableCell className="align-top text-sm">{e.location ? (e.location === "ClientPlace" ? "Client Place" : e.location) : "—"}</TableCell>
                      <TableCell className="align-top text-sm max-w-[240px]">
                        <div className="text-muted-foreground">{e.description || "—"}</div>
                        {e.status === "rejected" && e.manager_comment && (
                          <div className="text-xs mt-1 text-rag-red-fg">Rejected: {e.manager_comment}</div>
                        )}
                      </TableCell>
                      <TableCell className="align-top font-mono text-sm">{e.start_time}</TableCell>
                      <TableCell className="align-top font-mono text-sm">{e.end_time}</TableCell>
                      <TableCell className="align-top text-right font-mono">{e.total_hours}</TableCell>
                      <TableCell className="align-top">
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant="outline" className={STATUS_STYLES[e.status]}>{e.status}</Badge>
                          {e.late && <Badge variant="outline" className="bg-rag-red-bg text-rag-red-fg border-rag-red/30 text-[10px]" data-testid={`ts-late-badge-${e.id}`}>late</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="align-top text-right">
                        {["draft", "rejected"].includes(e.status) ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" onClick={() => editEntry(e)} data-testid={`ts-edit-${e.id}`}><PencilSimple size={15} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteEntry(e.id)} data-testid={`ts-delete-${e.id}`}><Trash size={15} /></Button>
                          </div>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={reqDialog} onOpenChange={(o) => { setReqDialog(o); if (!o) setReqReason(""); }}>
        <DialogContent data-testid="ts-late-request-dialog">
          <DialogHeader><DialogTitle>Request late submission</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">
            The window for <b>{date}</b> has closed. Tell your manager why so they can approve logging this day late.
          </DialogDescription>
          <Textarea rows={3} value={reqReason} onChange={(e) => setReqReason(e.target.value)}
            placeholder="Reason (e.g. was on leave, forgot to submit)" data-testid="ts-late-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReqDialog(false); setReqReason(""); }}>Cancel</Button>
            <Button onClick={requestLate} data-testid="ts-send-late-request-btn">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// 12-hour time picker (hh:mm + AM/PM) that reads/writes a 24h "HH:MM" string.
function TimeField({ value, onChange, testid }) {
  const { h12, m, ampm } = to12(value);
  const set = (nh, nm, na) => onChange(to24(nh, nm, na));
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  if (!mins.includes(m)) mins.push(m);
  mins.sort();
  return (
    <div className="flex gap-1" data-testid={testid}>
      <Select value={h12} onValueChange={(v) => set(v, m, ampm)}>
        <SelectTrigger className="px-2" data-testid={`${testid}-hour`}><SelectValue /></SelectTrigger>
        <SelectContent>{hours.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={m} onValueChange={(v) => set(h12, v, ampm)}>
        <SelectTrigger className="px-2" data-testid={`${testid}-min`}><SelectValue /></SelectTrigger>
        <SelectContent>{mins.map((mm) => <SelectItem key={mm} value={mm}>{mm}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={ampm} onValueChange={(v) => set(h12, m, v)}>
        <SelectTrigger className="px-2" data-testid={`${testid}-ampm`}><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
      </Select>
    </div>
  );
}
